import { fr } from "date-fns/locale/fr";
import DatePicker, {
  type ReactDatePickerProps,
  registerLocale,
  setDefaultLocale,
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import CalendarIcon from "$/lib/icons/CalendarIcon";
import ChevronIcon from "$/lib/icons/ChevronIcon";
import type { ThemeChoices } from "$/lib/types/General.types";
import { MONTHS, YEARS } from "$/lib/utils/constants/date.constants";
import { dateTimeFormatter } from "$/lib/utils/constants/intl.constants";
import { cn } from "$/lib/utils/functions/misc.functions";
import type { ReactNode } from "@tanstack/react-router";
import { getMonth } from "date-fns";
import Flexbox from "../../common/Flexbox";
import { Dropdown } from "../Dropdown";
import "./DatePickerInput.styles.css";

registerLocale("fr", fr);
setDefaultLocale("fr");

export type DatePickerInputProps<
  T extends boolean | undefined,
  R extends boolean | undefined,
> = ReactDatePickerProps<T, R> & {
  defaultLabel?: string;
  containerClassName?: string;
  calendarIcon?: ReactNode;
  customInputRightComponent?: ReactNode;
  theme?: ThemeChoices;
  parentContainerClassName?: string;
  topContainerClassName?: string;
  extraButtonComponent?: React.ReactNode;
};

const DatePickerInput = <
  WithRange extends boolean | undefined = undefined,
  WithMultiple extends boolean | undefined = undefined,
>({
  containerClassName,
  theme = "blue",
  parentContainerClassName,
  ...calendarProps
}: DatePickerInputProps<WithRange, WithMultiple>) => {
  const formattedSelectedDate = calendarProps.selected
    ? dateTimeFormatter.format(new Date(calendarProps.selected))
    : calendarProps.defaultLabel || "Date";

  return (
    <Flexbox
      className={cn(
        {
          "blue-theme": theme === "blue",
          "green-theme": theme === "green",
          "orange-theme": theme === "orange",
        },
        parentContainerClassName,
      )}
    >
      <DatePicker
        locale="fr"
        wrapperClassName={cn("w-fit", containerClassName)}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          changeYear,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <>
            <div className="mb-2 flex w-full items-center justify-between gap-2">
              <button
                type="button"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                className="flex h-10 w-10 rotate-180 items-center justify-center rounded border border-gray-300"
              >
                <ChevronIcon />
              </button>

              <div>
                <span className="text-base font-semibold">
                  {MONTHS[getMonth(monthDate)]}
                </span>
                <Dropdown
                  classNames={{
                    control: () =>
                      "!rounded !bg-white !text-sm !min-h-6 !text-secondary",
                  }}
                  placeholder="Année"
                  options={YEARS.map((year) => ({
                    label: year,
                    value: year,
                  }))}
                  onChange={(option) =>
                    option && changeYear(Number(option.value))
                  }
                  value={monthDate.getFullYear().toString()}
                  theme={theme}
                />
              </div>

              <button
                type="button"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                className="flex h-10 w-10 items-center justify-center rounded border border-gray-300"
              >
                <ChevronIcon />
              </button>
            </div>
            <div className="mt-4 border border-gray-100" />
          </>
        )}
        customInput={
          <button
            type="button"
            className="bg-blue-light flex items-center gap-2 whitespace-nowrap text-black"
          >
            {calendarProps.calendarIcon ? (
              calendarProps.calendarIcon
            ) : (
              <CalendarIcon fill="black" />
            )}
            <span className="font-bold">{formattedSelectedDate}</span>
            {calendarProps.customInputRightComponent}
          </button>
        }
        {...calendarProps}
      />
    </Flexbox>
  );
};

DatePickerInput.displayName = "DatePickerInput";

export default DatePickerInput;
